import { Autocomplete, Divider, Paper, TextField, Drawer, Box } from "@mui/material"
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { usePageLoader } from "../contexts/page_content_context";
import { usePageStartBootstrap } from "../hooks/use_page_start_bootstrap";
import { zodResolver } from "@hookform/resolvers/zod";
import { DropDown } from "../domain/models/dropdown";
import { FormSwitchInput } from "./masked_inputs/react_forms/form_switch_input";
import { BancoPagamentoForm, BancoPagamentoSchema, defaultBancoPagamentoForm } from "../../features/financeiro/presentation/schemas/BancoPagamento_lista/BancoPagamento_form";

import DynamicIcon from "./dynamic_icon";
import DefaultFormInput from "./default_form_input";
import PrimaryButton from "./primary_button";

/**
 * DropDownBancoPagamento
 */

type Props<F extends {}> = {
  name: keyof F,
  label?: string,
  newButtonLabel?: string,
  required?: boolean,
  readOnly?: boolean,
  placeholder?: string,
  reactHookForm: UseFormReturn<F>,

};

export const DropDownBanco = <F extends {},>({
  name,
  label,
  newButtonLabel,
  required,
  readOnly,
  placeholder,
  reactHookForm,

}: Props<F>) => {


  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { pushErrorNotification, pushInfoNotification } = usePageLoader();
  const [BancoPagamentoDropDown, setBancoPagamentoDropDown] = useState<DropDown[]>([]);

  const {
    commons: {
      getBancosPagamentoDropDownUseCase,
    },
    financeiro: {
      createBancoPagamentoUseCase,
    }

  } = useDependencyInjection();

  const hookForm = useForm<BancoPagamentoForm>({
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
    defaultValues: defaultBancoPagamentoForm,
    resolver: zodResolver(BancoPagamentoSchema),
  });

  const loadBancoPagamentoDropDown = useCallback(async () => {
    try {
      const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
      setBancoPagamentoDropDown(result);
    } catch (e: unknown) {
      throw new Error('Falha ao buscar Banco!');
    }
  }, [
    getBancosPagamentoDropDownUseCase,
  ]);


  usePageStartBootstrap(async () => {
    loadBancoPagamentoDropDown();

  })

  const hanldleSalvarBancoPagamento = useCallback(async (form: BancoPagamentoForm) => {
    try {
      setSaving(true);

      const idBancoPagamento = await createBancoPagamentoUseCase.createBancoPagamento({
        idBancoPagamento: 0,
        ativo: form.ativo,
        controlaSaldo: form.controlaSaldo,
        nomeBanco: form.nomeBanco,
        nrAgencia: form.nrAgencia,
        nrConta: form.nrConta,
      })

      reactHookForm.setValue(name as any, idBancoPagamento as any);
      pushInfoNotification("SUCESSO", "Banco criado com sucesso!!", { isAutoFinish: true })

    } catch (e: unknown) {
      pushErrorNotification("Erro", ["Falha ao salvar banco"], { isAutoFinish: true })
    } finally {
      setSaving(false);
      loadBancoPagamentoDropDown();
      setOpenModal(false);
      hookForm.reset();
    }
  }, [
    name,
    reactHookForm,
    hookForm,
    createBancoPagamentoUseCase,
    pushInfoNotification,
    pushErrorNotification,
    loadBancoPagamentoDropDown]);



  //  BOTÃO 
  const CustomPaper = useMemo(
    () =>
      ({ children, ...props }: any) => (
        <Paper {...props}>
          <div>
            {children}
            <Divider />
            <div>
              <div
                className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold flex items-center gap-2 text-sm text-primary-green hover:bg-hover-light-gray"
                onMouseDown={() => setOpenModal(true)}
                role="button"
                tabIndex={0}>
                <DynamicIcon
                  nameIcon="Add"
                  size={20}
                  className="text-primary-green"
                />
                <span>{newButtonLabel ?? "Novo"}</span>
              </div>
            </div>
          </div>
        </Paper>
      ),
    [newButtonLabel]
  );


  return (
    <>

      {/* DROPDOWN */}

      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (formState.errors[name as keyof typeof formState.errors]) {
            const fieldError = formState.errors[name] as any;
            if (fieldError?.message && typeof fieldError.message === 'string') {
              helperText = fieldError.message;
            }
          }


          return (
            <Autocomplete
              fullWidth={true}
              size="small"
              disablePortal
              options={BancoPagamentoDropDown}
              getOptionKey={opt => opt.id}
              getOptionLabel={(opt) => opt.title}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              slots={{
                paper: CustomPaper,
              }}
              sx={{ minHeight: 45 }}
              readOnly={readOnly}
              disabled={formState.disabled}
              value={BancoPagamentoDropDown.find(x => x.id === field.value) ?? null}
              onChange={(_, newValue) => field.onChange(newValue ? newValue.id : undefined)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={label}
                  error={helperText !== null}
                  helperText={helperText}
                  placeholder={placeholder}
                  required={required}
                />
              )}
            />
          );
        }}
      />

      {/* MODAL DE CADASTRO */}

      <Drawer
        anchor="right"
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{ zIndex: 1301 }}
        PaperProps={{ style: { width: 500, padding: 20, display: "flex", flexDirection: "column", height: "100%" } }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <h2 className="text-lg font-semibold mb-4">Adicionar Banco</h2>


          <div className="flex flex-col items-start w-[25rem] gap-4">

            <div className="w-full p-4 rounded-2xl shadow-sm">
              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-center justify-between">
                  <label className="font-medium text-gray-700">Status:</label>
                  <FormSwitchInput
                    name="ativo"
                    readOnly={saving}
                    reactHookForm={hookForm}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="font-medium text-gray-700">Controla Saldo:</label>
                  <FormSwitchInput
                    activeLabel="Sim"
                    inactiveLabel="Não"
                    name="controlaSaldo"
                    readOnly={saving}
                    reactHookForm={hookForm}
                  />
                </div>
              </div>
            </div>

            <DefaultFormInput
              name='nomeBanco'
              label='Nome do Banco'
              readOnly={saving}
              multiline={false}
              reactHookForm={hookForm}
            />

            <DefaultFormInput
              name='nrAgencia'
              label='Nº  da Agência '
              readOnly={saving}
              reactHookForm={hookForm}
            />

            <DefaultFormInput
              name='nrConta'
              label='Nº da Conta '
              readOnly={saving}
              reactHookForm={hookForm}
            />

          </div>
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "white",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >

          <Box
            className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
            onClick={() => setOpenModal(false)}
          >
            <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
          </Box>

          <Box width="120px">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">
                Salvando...
              </PrimaryButton>
            ) : (
              <PrimaryButton
                buttonColor="primary-green"
                onClick={hookForm.handleSubmit(hanldleSalvarBancoPagamento)}
              >
                Salvar
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Drawer>

    </>
  );
};
